import React, { useState, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description,
  Description as PdfIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as ProcessingIcon,
} from '@mui/icons-material';

import { convertPdfToBase64Images, isPdfFile } from '../utils/pdfToImageConverter';

// Supported file types and their configurations
const SUPPORTED_FILE_TYPES: Record<string, { extensions: string[]; maxSize: number; useBatch: boolean }> = {
  'application/pdf': { extensions: ['.pdf'], maxSize: 100 * 1024 * 1024, useBatch: true },
  'image/png': { extensions: ['.png'], maxSize: 10 * 1024 * 1024, useBatch: false },
  'image/jpeg': { extensions: ['.jpg', '.jpeg'], maxSize: 10 * 1024 * 1024, useBatch: false },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { extensions: ['.xlsx'], maxSize: 20 * 1024 * 1024, useBatch: false },
  'application/vnd.ms-excel': { extensions: ['.xls'], maxSize: 20 * 1024 * 1024, useBatch: false },
  'message/rfc822': { extensions: ['.eml'], maxSize: 50 * 1024 * 1024, useBatch: false },
  'text/plain': { extensions: ['.txt'], maxSize: 5 * 1024 * 1024, useBatch: false },
  'text/csv': { extensions: ['.csv'], maxSize: 10 * 1024 * 1024, useBatch: false },
};

const ACCEPTED_EXTENSIONS = Object.values(SUPPORTED_FILE_TYPES).flatMap(type => type.extensions).join(',');

const isFileSupported = (file: File): boolean => {
  const fileType = SUPPORTED_FILE_TYPES[file.type];
  if (!fileType) return false;

  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  return fileType.extensions.includes(extension) && file.size <= fileType.maxSize;
};

const shouldUseBatchUpload = (file: File): boolean => {
  return SUPPORTED_FILE_TYPES[file.type]?.useBatch || false;
};

interface UploadFile {
  id: string;
  file: File;
  status: 'converting' | 'uploading' | 'completed' | 'failed';
  progress: number;
  error?: string;
  totalPages?: number;
  completedPages?: number;
}

interface DocumentUploadComponentProps {
  onFileComplete?: (fileId: string) => void;
  onRefreshHistory?: () => void;
}

const DocumentUploadComponent: React.FC<DocumentUploadComponentProps> = ({
  onFileComplete,
  onRefreshHistory,
}) => {
  const { getIdTokenClaims, user } = useAuth0();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFiles, setUploadFiles] = useState<Map<string, UploadFile>>(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'auto' (default): the backend samples the first pages and picks SINGLE or MULTI for the user.
  const [pdfProcessingMode, setPdfProcessingMode] = useState<'auto' | 'multi' | 'single'>('auto');

  const updateFileState = useCallback((fileId: string, updates: Partial<UploadFile>) => {
    setUploadFiles(prev => {
      const newMap = new Map(prev);
      const file = newMap.get(fileId);
      if (file) {
        newMap.set(fileId, { ...file, ...updates });
      }
      return newMap;
    });
  }, []);

  const uploadSequentially = useCallback(async (fileId: string, file: File) => {
    const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';
    const BATCH_SIZE = 5; // Request presigned URLs for 5 pages at a time

    try {
      // Check if this is a PDF that needs batch processing (only in multi-document mode)
      if (shouldUseBatchUpload(file) && pdfProcessingMode !== 'single') {
        // Convert PDF to images for multi-document processing
        updateFileState(fileId, { status: 'converting', progress: 10 });
        const base64Images = await convertPdfToBase64Images(file, getIdTokenClaims, 0.95, pdfProcessingMode);

      // Check if this is an async processing response
      if (base64Images.length === 1 && (base64Images[0] as any).asyncProcessing) {
        const asyncInfo = base64Images[0] as any;
        updateFileState(fileId, {
          status: 'completed',
          progress: 100,
          error: asyncInfo.message // Use the error field to display the message
        });

        // Show success message to user
        console.log(`[PDFUpload] ${asyncInfo.message}`);
        return; // Exit early - processing is happening in background
      }

      updateFileState(fileId, {
        status: 'uploading',
        progress: 30,
        totalPages: base64Images.length,
        completedPages: 0
      });

      // Get ID token (same as all other API calls)
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;


      const failedPages: number[] = [];
      let successCount = 0;

      // Process pages in batches - get presigned URLs for multiple pages at once
      for (let batchStart = 0; batchStart < base64Images.length; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, base64Images.length);
        const batchImages = base64Images.slice(batchStart, batchEnd);

        console.log(`Processing batch: pages ${batchStart + 1} to ${batchEnd}`);

        // Build batch request for presigned URLs
        const batchFiles = batchImages.map(img => ({
          filename: img.fileName,
          contentType: 'image/png'
        }));

        try {
          // Get batch presigned URLs with retry
          let batchResponse = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              const response = await fetch(`${API_ENDPOINT}/batch-upload-urls`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ files: batchFiles })
              });

              if (response.ok) {
                batchResponse = await response.json();
                break;
              }

              console.warn(`Batch presigned URL request failed with status ${response.status}, attempt ${attempt + 1}/3`);
              if (attempt < 2) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
              }
            } catch (error) {
              console.error(`Batch presigned URL request error on attempt ${attempt + 1}:`, error);
              if (attempt < 2) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
              }
            }
          }

          if (!batchResponse || !batchResponse.urls) {
            throw new Error('Failed to get batch presigned URLs after 3 attempts');
          }

          // Upload each page using the presigned URLs
          for (let i = 0; i < batchImages.length; i++) {
            const pageIndex = batchStart + i;
            const img = batchImages[i];
            const urlInfo = batchResponse.urls[i];
            const progress = 30 + ((pageIndex + 1) / base64Images.length) * 50; // 30% to 80%

            updateFileState(fileId, { progress, completedPages: pageIndex + 1 });

            // Convert base64 to blob
            const byteCharacters = atob(img.data.split(',')[1]);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/png' });

            // Upload to S3 with retry
            let uploadSuccess = false;
            for (let attempt = 0; attempt < 3; attempt++) {
              try {
                const s3Headers: HeadersInit = {
                  'Content-Type': 'image/png'
                };

                // Add metadata headers if provided
                if (urlInfo.requiredMetadata) {
                  Object.keys(urlInfo.requiredMetadata).forEach(key => {
                    s3Headers[`x-amz-meta-${key}`] = urlInfo.requiredMetadata[key];
                  });
                }

                const uploadResponse = await fetch(urlInfo.uploadUrl, {
                  method: 'PUT',
                  body: blob,
                  headers: s3Headers
                });

                if (uploadResponse.ok) {
                  console.log(`Page ${pageIndex + 1} uploaded successfully`);
                  uploadSuccess = true;
                  successCount++;
                  break;
                }

                console.warn(`Page ${pageIndex + 1} upload failed with status ${uploadResponse.status}, attempt ${attempt + 1}/3`);
                if (attempt < 2) {
                  await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
                }
              } catch (error) {
                console.error(`Page ${pageIndex + 1} upload error on attempt ${attempt + 1}:`, error);
                if (attempt < 2) {
                  await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
                }
              }
            }

            if (!uploadSuccess) {
              console.error(`Failed to upload page ${pageIndex + 1} after all retries`);
              failedPages.push(pageIndex);
            }

            // Small delay between S3 uploads within batch
            if (i < batchImages.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
        } catch (batchError) {
          console.error(`Batch processing failed:`, batchError);
          // Add all pages in this batch to failed pages
          for (let i = 0; i < batchImages.length; i++) {
            failedPages.push(batchStart + i);
          }
        }

        // Small delay between batches
        if (batchEnd < base64Images.length) {
          console.log(`Processing next batch...`);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Retry failed pages in smaller batches
      if (failedPages.length > 0) {
        console.log(`Retrying ${failedPages.length} failed pages...`);
        updateFileState(fileId, {
          status: 'uploading',
          progress: 85,
          error: `Retrying ${failedPages.length} failed pages...`
        });

        // Retry in smaller batches of 3
        const retryBatchSize = 3;
        let retriedCount = 0;

        for (let i = 0; i < failedPages.length; i += retryBatchSize) {
          const batchPageIndices = failedPages.slice(i, i + retryBatchSize);
          const retryImages = batchPageIndices.map(idx => base64Images[idx]);

          // Get batch presigned URLs for retry
          const retryFiles = retryImages.map(img => ({
            filename: img.fileName,
            contentType: 'image/png'
          }));

          try {
            const retryResponse = await fetch(`${API_ENDPOINT}/batch-upload-urls`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify({ files: retryFiles })
            });

            if (!retryResponse.ok) {
              console.error(`Failed to get retry presigned URLs: ${retryResponse.status}`);
              continue;
            }

            const retryBatch = await retryResponse.json();

            // Upload each page in retry batch
            for (let j = 0; j < retryImages.length; j++) {
              const img = retryImages[j];
              const urlInfo = retryBatch.urls[j];
              const pageIndex = batchPageIndices[j];

              // Convert base64 to blob
              const byteCharacters = atob(img.data.split(',')[1]);
              const byteNumbers = new Array(byteCharacters.length);
              for (let k = 0; k < byteCharacters.length; k++) {
                byteNumbers[k] = byteCharacters.charCodeAt(k);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: 'image/png' });

              // Upload with single retry
              for (let attempt = 0; attempt < 2; attempt++) {
                try {
                  const s3Headers: HeadersInit = {
                    'Content-Type': 'image/png'
                  };

                  if (urlInfo.requiredMetadata) {
                    Object.keys(urlInfo.requiredMetadata).forEach(key => {
                      s3Headers[`x-amz-meta-${key}`] = urlInfo.requiredMetadata[key];
                    });
                  }

                  const uploadResponse = await fetch(urlInfo.uploadUrl, {
                    method: 'PUT',
                    body: blob,
                    headers: s3Headers
                  });

                  if (uploadResponse.ok) {
                    console.log(`Retry: Page ${pageIndex + 1} uploaded successfully`);
                    successCount++;
                    retriedCount++;
                    break;
                  }
                } catch (error) {
                  console.error(`Retry failed for page ${pageIndex + 1}:`, error);
                }

                if (attempt === 0) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              }

              // Small delay between uploads
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          } catch (error) {
            console.error(`Retry batch failed:`, error);
          }

          // Delay between retry batches
          if (i + retryBatchSize < failedPages.length) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        console.log(`Retry complete: ${retriedCount} of ${failedPages.length} pages recovered`);
      }

      // Final status update
      if (successCount === base64Images.length) {
        updateFileState(fileId, { status: 'completed', progress: 100 });
        console.log(`All ${base64Images.length} pages uploaded successfully!`);
      } else if (successCount > 0) {
        updateFileState(fileId, {
          status: 'completed',
          progress: 95,
          error: `Uploaded ${successCount}/${base64Images.length} pages. ${failedPages.length} failed.`
        });
        console.warn(`Partial success: ${successCount}/${base64Images.length} pages uploaded`);
      } else {
        updateFileState(fileId, {
          status: 'failed',
          error: 'All pages failed to upload'
        });
        console.error('Complete failure: No pages uploaded');
      }

      if (onFileComplete) onFileComplete(fileId);
      if (onRefreshHistory) onRefreshHistory();

      } else {
        // Handle non-PDF files (direct upload without conversion)
        updateFileState(fileId, { status: 'uploading', progress: 20 });

        // Get ID token
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw;

        // Get presigned URL for single file
        const response = await fetch(`${API_ENDPOINT}/upload-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            pdfProcessingMode: file.type === 'application/pdf' ? pdfProcessingMode : undefined
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to get upload URL: ${response.status}`);
        }

        const { uploadUrl, key, requiredMetadata } = await response.json();
        updateFileState(fileId, { status: 'uploading', progress: 50 });

        // Read file as ArrayBuffer for non-PDF files
        const fileBuffer = await file.arrayBuffer();

        // Prepare headers
        const s3Headers: HeadersInit = {
          'Content-Type': file.type || 'application/octet-stream'
        };

        if (requiredMetadata) {
          Object.keys(requiredMetadata).forEach(metaKey => {
            s3Headers[`x-amz-meta-${metaKey}`] = requiredMetadata[metaKey];
          });
        }

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: fileBuffer,
          headers: s3Headers
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload to S3 failed: ${uploadResponse.status}`);
        }

        updateFileState(fileId, { status: 'completed', progress: 100 });
        console.log(`File uploaded successfully: ${file.name}`);

        if (onFileComplete) onFileComplete(fileId);
        if (onRefreshHistory) onRefreshHistory();
      }

    } catch (error) {
      console.error('Upload failed:', error);
      updateFileState(fileId, {
        status: 'failed',
        error: `Upload failed: ${error}`
      });
    }
  }, [getIdTokenClaims, updateFileState, onFileComplete, onRefreshHistory, pdfProcessingMode]);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files) return;

    setError(null);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!isFileSupported(file)) {
        setError(`File type not supported or file too large: ${file.name}`);
        continue;
      }

      const fileId = `${Date.now()}-${file.name}`;
      const uploadFile: UploadFile = {
        id: fileId,
        file,
        status: 'converting',
        progress: 0,
      };

      setUploadFiles(prev => new Map(prev.set(fileId, uploadFile)));

      // Start upload
      uploadSequentially(fileId, file);
    }
  }, [uploadSequentially]);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files);
  }, [handleFileSelect]);

  const getStatusColor = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'uploading': return 'warning';
      case 'converting': return 'info';
      default: return 'default';
    }
  };

  const uploadFilesArray = Array.from(uploadFiles.values());

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 3,
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'grey.300',
          backgroundColor: isDragging ? 'primary.light' : 'grey.50',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.light',
          },
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6" align="center">
            Document Upload
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            Drop files here or click to select
          </Typography>
          <Typography variant="caption" align="center" color="text.secondary" sx={{ mt: 1 }}>
            Supported: PDF, PNG, JPG, Excel (XLS/XLSX), EML, TXT, CSV
          </Typography>
        </Box>
      </Paper>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* PDF Processing Mode: Auto-detect (default) with manual overrides */}
      <Paper elevation={1} sx={{ mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            PDF Processing
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={pdfProcessingMode}
            onChange={(_e, v) => { if (v) setPdfProcessingMode(v); }}
          >
            <ToggleButton value="auto">
              <Tooltip title="Recommended. Cal reads the first pages and decides whether the PDF is one multi-page report or a bundle of separate reports (e.g. a scan of many calibration certificates)."><span>Auto-detect</span></Tooltip>
            </ToggleButton>
            <ToggleButton value="multi">
              <Tooltip title="Force: every page is treated as a separate report."><span>Multiple documents</span></Tooltip>
            </ToggleButton>
            <ToggleButton value="single">
              <Tooltip title="Force: all pages are processed together as one report."><span>Single document</span></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {pdfProcessingMode === 'auto'
            ? 'Auto-detect: a bundle of certificates becomes one report per page; a multi-page report stays one document. Override only if the result looks wrong.'
            : pdfProcessingMode === 'multi'
              ? 'Multiple documents: each page will be processed as a separate proving report.'
              : 'Single document: all pages will be processed together as one document.'}
        </Typography>
      </Paper>

      {uploadFilesArray.length > 0 && (
        <Paper elevation={1} sx={{ mt: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Upload Queue ({uploadFilesArray.length} files)
            </Typography>
          </Box>

          <List>
            {uploadFilesArray.map((uploadFile) => (
              <ListItem key={uploadFile.id} divider>
                <ListItemIcon>
                  {isPdfFile(uploadFile.file) ? (
                    <PdfIcon color="error" />
                  ) : (
                    <Description color="primary" />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        {uploadFile.file.name}
                      </Typography>
                      <Chip
                        label={uploadFile.status}
                        size="small"
                        color={getStatusColor(uploadFile.status)}
                      />
                      {uploadFile.totalPages && (
                        <Chip
                          label={`${uploadFile.completedPages || 0}/${uploadFile.totalPages} pages`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {(uploadFile.file.size / 1024 / 1024).toFixed(2)} MB
                        {uploadFile.error && ` • Error: ${uploadFile.error}`}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={uploadFile.progress}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  }
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {uploadFile.status === 'completed' && <CheckCircleIcon color="success" />}
                  {uploadFile.status === 'failed' && <ErrorIcon color="error" />}
                  {uploadFile.status === 'uploading' && <ProcessingIcon color="info" />}
                  {uploadFile.status === 'converting' && <ProcessingIcon color="info" />}
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default DocumentUploadComponent;